/**
 * 主数据字典类型。对应 md_code 表的 code_type 列。
 *
 * 这些字典的权威来源是 SAP，本系统只做本地缓存。新增字典类型时同步补 seed-master.ts。
 */
export const CODE_TYPE = {
    /** 使用部位，如 10A = 大身面料(1) */
    USED_PART: 'USED_PART',
    /** 材料分类，如 E = 开发代码 */
    MATERIAL_GROUP: 'MATERIAL_GROUP',
    /** Tag 颜色（吊牌颜色），com = 全色共通 */
    TAG_COLOR: 'TAG_COLOR',
    /** 配色部位 A~G，对应 ColorWay 的七列 */
    COLOR_MATCH_PART: 'COLOR_MATCH_PART',
    /** 辅料种类，如 318 = Label */
    SUB_MATERIAL_TYPE: 'SUB_MATERIAL_TYPE',
    /** 样品部位（基准尺寸与尺寸偏差用） */
    PATTERN_PART: 'PATTERN_PART',
    UNIT: 'UNIT',
    CURRENCY: 'CURRENCY',
    GENDER: 'GENDER',
    /** 生产品类，如 12 = 衬衫 */
    ITEM: 'ITEM',
    APPAREL_TYPE: 'APPAREL_TYPE',
    L_CATEGORY: 'L_CATEGORY',
    M_CATEGORY: 'M_CATEGORY',
    PRODUCT_YEAR: 'PRODUCT_YEAR',
    SEASON: 'SEASON',
    SALES_MONTH: 'SALES_MONTH',
    // 07 在线商品信息的六组商品属性。
    // 这一节没有任何 SAP 接口，取值来自参考系统 drap-dev 的 STEP 07 画面。
    // 放进字典而不是写成前端常量 —— 它们是电商平台的商品属性，会随平台规则变。
    /** 洗涤方法 세탁방법 */
    ONLINE_WASH: 'ONLINE_WASH',
    /** 版型 핏 */
    ONLINE_FIT: 'ONLINE_FIT',
    /** 弹性 신축성 */
    ONLINE_ELASTIC: 'ONLINE_ELASTIC',
    /** 厚度 두께감 */
    ONLINE_THICKNESS: 'ONLINE_THICKNESS',
    /** 里布 안감 */
    ONLINE_LINING: 'ONLINE_LINING',
    /** 透明度 비침 */
    ONLINE_SHEER: 'ONLINE_SHEER',
    // 06 QC 委托书。对应 SP0310 的 QCTYPE / CONFIRM_RESULT / REJECT_REASON。
    // **码值表接口文档里没给**（SP0310 那 26 行的「示例数据」「备注」两列全是空的），
    // 待后端团队提供后 seed 即可，字典留空时画面正常显示，只是下拉是空的。
    /** QC 类型 */
    QC_TYPE: 'QC_TYPE',
    /** QC 确认结果 */
    QC_RESULT: 'QC_RESULT',
    /** QC 驳回原因 */
    QC_REJECT_REASON: 'QC_REJECT_REASON',
};
/** 表示「全色共通」的 Tag 颜色码。主材料不区分颜色时用它。 */
export const TAG_COLOR_COMMON = 'com';
/**
 * ColorWay 的配色部位（表格列）。
 *
 * 2026-10-05 用户调整：**默认 A~D 四列**，用「增加列」按钮往后加，不再固定七列。
 * 数据模型存的是 `Record<string, {cd, nm}>`（部位字母为键），所以加列不需要迁移 ——
 * 后端与 Tech Pack 都是按实际用到的键渲染。
 *
 * **上限就是 7（A~G）** —— `ds_colorway` 表就只有 a~g 七组
 * colorCd/colorNm 固定列（见 schema.prisma），加到 H 要改表结构 + 迁移。
 * 所以「增加列」最多点到G，别做成26 —— 那会让用户以为能加，加了却存不住。
 */
export const COLOR_MATCH_PART_LETTERS = 'ABCDEFG';
/** 默认可用列数（用户指定 A~D 四列） */
export const COLOR_MATCH_PARTS_DEFAULT = 4;
export const COLOR_MATCH_PARTS_MAX = COLOR_MATCH_PART_LETTERS.length;
/** 前 n 个部位字母，如 partLetters(4) → ['A','B','C','D'] */
export function partLetters(n) {
    return Array.from(COLOR_MATCH_PART_LETTERS.slice(0, Math.max(0, Math.min(n, COLOR_MATCH_PARTS_MAX))));
}
/**
 * 旧的固定七列。保留导出是因为别处（预填、Tech Pack 单测）还在引用，
 * 新代码请用 `partLetters()` —— 它按界面实际列数渲染。
 */
export const COLOR_MATCH_PARTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
/** 设计 Memo 的固定行数。旧系统固定 6 行。 */
export const DESIGN_MEMO_ROWS = 6;
/** 08 节核心卖点的默认行数。参考系统固定 3 条，这里只是界面上预留的空行数，不是上限。 */
export const USP_POINT_ROWS = 3;
/**
 * QC 评论的来源方。参考系统的卡片标题是「QC 코멘트 — 공장 · 본사」，双方都能写。
 * 本期按「本社代录」做（工厂没有本系统账号），但字段先区分开 ——
 * 将来开放工厂直填时只加权限与画面，不动数据模型。
 */
export const QC_SIDE = { FACTORY: 'FACTORY', HQ: 'HQ' };
/** 07 节的尺寸单位。参考系统画面上是一个 CM / INCH 下拉。 */
export const ONLINE_SIZE_UNIT = { CM: 'CM', INCH: 'INCH' };
// ---------------------------------------------------------------------------
// Tech Pack 相关
// ---------------------------------------------------------------------------
/**
 * SAP 来源的国别 / 地区代码。中国区委托书数量固定为 CN，页面不再按国别拆分。
 */
export const COUNTRY = {
    KR: 'KR',
    CN: 'CN',
    HK: 'HK',
    TW: 'TW',
    MY: 'MY',
};
export const COUNTRY_ORDER = [
    COUNTRY.KR,
    COUNTRY.CN,
    COUNTRY.HK,
    COUNTRY.TW,
    COUNTRY.MY,
];
/**
 * 担当角色。Tech Pack 每一页页眉右上角那一排。
 * 前 7 个来自 SAP 款式主数据的人员编号，技术包担当由本系统指定。
 */
export const CHARGE_ROLE = {
    /** 디자인 设计 */
    DESIGN: 'DESIGN',
    /** 기획 企划 */
    PLAN: 'PLAN',
    /** 원자재 原材料 */
    FABRIC: 'FABRIC',
    /** 부자재 辅料 */
    TRIM: 'TRIM',
    /** 생산 生产 */
    PRODUCTION: 'PRODUCTION',
    /** 패턴 版型 */
    PATTERN: 'PATTERN',
    /** CAD */
    CAD: 'CAD',
    /** 텍펙 技术包 */
    TECHPACK: 'TECHPACK',
};
/** 页眉里担当的排列顺序，与打印件一致 */
export const CHARGE_ROLE_ORDER = [
    CHARGE_ROLE.DESIGN,
    CHARGE_ROLE.PLAN,
    CHARGE_ROLE.FABRIC,
    CHARGE_ROLE.TRIM,
    CHARGE_ROLE.PRODUCTION,
    CHARGE_ROLE.PATTERN,
    CHARGE_ROLE.CAD,
    CHARGE_ROLE.TECHPACK,
];
/** 担当角色 → 款式主数据里对应的人员编号字段。TECHPACK 没有来源，由本系统指定。 */
export const CHARGE_ROLE_STYLE_FIELD = {
    [CHARGE_ROLE.DESIGN]: 'designerNo',
    [CHARGE_ROLE.PLAN]: 'plannerNo',
    [CHARGE_ROLE.FABRIC]: 'fabricPurNo',
    [CHARGE_ROLE.TRIM]: 'trimPurNo',
    [CHARGE_ROLE.PRODUCTION]: 'productionNo',
    [CHARGE_ROLE.PATTERN]: 'patternNo',
    [CHARGE_ROLE.CAD]: 'cadNo',
    [CHARGE_ROLE.TECHPACK]: null,
};
/**
 * 辅料 BOM 的分组。字段完全相同，但 Tech Pack 第 5、6 页是分开的两张表。
 */
export const BOM_GROUP = {
    /** 부자재 辅料 */
    TRIM: 'TRIM',
    /** 재봉사 缝纫线 */
    THREAD: 'THREAD',
};
//# sourceMappingURL=codes.js.map